import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtQty, fmtCHF } from "@/lib/format";
import { Field, Sel, opts } from "@/components/Field";
import { FormDialog, useSubmit, num, clean } from "./FormDialog";

const newRow = () => ({ key: Math.random().toString(36).slice(2), product_id: "", lot_id: "", quantity: "", unit_price: "", lots: [] });

const ExitRow = ({ row, idx, products, withPrice, onChange, onRemove, canRemove }) => {
  const product = products.find((p) => p.id === row.product_id);
  useEffect(() => {
    if (!row.product_id) return;
    api.get(`/products/${row.product_id}`).then((r) => {
      const ls = r.data.lots.filter((l) => l.qty_remaining > 0);
      onChange({ lots: ls, lot_id: (ls.find((l) => l.suggested) || ls[0])?.id || "", unit_price: r.data.sale_price ?? "" });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.product_id]);
  const lotOpts = row.lots.map((l) => ({ value: l.id, label: `${l.lot_number || "Senza numero"} · ${fmtQty(l.qty_remaining)} disp.${l.expiry_date ? ` · scad. ${fmtDate(l.expiry_date)}` : ""}` }));
  return (
    <div data-testid={`multi-exit-row-${idx}`} className="rounded-lg border border-slate-200 p-3 space-y-3">
      <div className="flex items-end gap-2">
        <Field label={`Prodotto ${idx + 1} *`} className="flex-1">
          <Sel testId={`multi-exit-product-${idx}`} value={row.product_id} onChange={(v) => onChange({ product_id: v, lot_id: "", lots: [] })} placeholder="Scegli un prodotto"
            options={products.map((p) => ({ value: p.id, label: `${p.name} (${fmtQty(p.quantity)} ${p.unit})` }))} />
        </Field>
        {canRemove && <Button type="button" data-testid={`multi-exit-remove-${idx}`} variant="ghost" size="icon" className="h-11 w-11 text-red-700" onClick={onRemove} aria-label="Rimuovi"><Trash2 className="h-4 w-4" /></Button>}
      </div>
      <Field label="Lotto *" hint={row.product_id && !row.lots.length ? "Nessun lotto disponibile" : ""}>
        <Sel testId={`multi-exit-lot-${idx}`} value={row.lot_id} onChange={(v) => onChange({ lot_id: v })} placeholder="Scegli il lotto" options={lotOpts} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={`Quantità *${product ? ` (${product.unit})` : ""}`}>
          <Input data-testid={`multi-exit-qty-${idx}`} type="number" min="0" step="any" required value={row.quantity} onChange={(e) => onChange({ quantity: e.target.value })} className="h-11" />
        </Field>
        {withPrice && (
          <Field label="Prezzo unit. (CHF) *" hint={row.unit_price !== "" && row.quantity ? `Riga ${fmtCHF(Number(row.unit_price) * Number(row.quantity))}` : ""}>
            <Input data-testid={`multi-exit-price-${idx}`} type="number" min="0" step="0.05" required value={row.unit_price} onChange={(e) => onChange({ unit_price: e.target.value })} className="h-11" />
          </Field>
        )}
      </div>
    </div>
  );
};

const InvoiceFields = ({ inv, setInv }) => {
  const s = (k) => (e) => setInv({ ...inv, [k]: e.target.value });
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="Intestatario *" className="col-span-2"><Input data-testid="multi-invoice-name-input" required value={inv.recipient_name} onChange={s("recipient_name")} className="h-11" /></Field>
      <Field label="E-mail *" className="col-span-2"><Input data-testid="multi-invoice-email-input" type="email" required value={inv.email} onChange={s("email")} className="h-11" /></Field>
      <Field label="Via e numero" className="col-span-2"><Input data-testid="multi-invoice-street-input" value={inv.street} onChange={s("street")} className="h-11" /></Field>
      <Field label="NPA"><Input data-testid="multi-invoice-pcode-input" value={inv.pcode} onChange={s("pcode")} className="h-11" /></Field>
      <Field label="Località"><Input data-testid="multi-invoice-city-input" value={inv.city} onChange={s("city")} className="h-11" /></Field>
    </div>
  );
};

export const MultiExitDialog = ({ open, onOpenChange, onSaved }) => {
  const { assoc, user, can } = useAuth();
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const [rows, setRows] = useState([newRow()]);
  const [f, setF] = useState({});
  const [inv, setInv] = useState(null);
  const { saving, run } = useSubmit();
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    if (!open) return;
    setRows([newRow()]);
    setInv(null);
    setF({ reason: "", picked_up_by: user?.name || "", recipient: "", event_id: "", document: "", notes: "" });
    api.get("/products?page_size=200&status=active").then((r) => setProducts(r.data.items.filter((p) => p.quantity > 0)));
    api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, [open, user]);

  const patch = (key) => (p) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)));
  const total = rows.reduce((t, r) => t + (Number(r.unit_price) || 0) * (Number(r.quantity) || 0), 0);

  const submit = () =>
    run(async () => {
      if (rows.some((r) => !r.product_id || !r.lot_id)) throw { message: "x", response: { data: { detail: "Scegli prodotto e lotto per ogni riga" } } };
      const items = rows.map((r) => clean({ product_id: r.product_id, lot_id: r.lot_id, quantity: num(r.quantity), unit_price: inv ? num(r.unit_price) : null }));
      const { data } = await api.post("/movements/out-multi", clean({ ...f, items, invoice: inv ? clean(inv) : null }));
      if (data.invoice_number) {
        const msg = { sent: "inviata per e-mail", not_configured: "creata ma NON inviata: servizio e-mail non configurato", failed: "creata ma invio e-mail non riuscito" }[data.invoice_email_status];
        toast[data.invoice_email_status === "sent" ? "success" : "warning"](`Fattura ${data.invoice_number} (${fmtCHF(data.invoice_total)}) ${msg}`, { duration: 8000 });
      }
      onOpenChange(false);
      onSaved?.();
    }, "Uscita multipla registrata");

  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title="Uscita multipla" description="Più prodotti in un'unica uscita, con una sola fattura facoltativa." onSubmit={submit} saving={saving} testId="multi-exit-dialog" submitLabel="Registra uscita" wide>
      {rows.map((r, i) => (
        <ExitRow key={r.key} row={r} idx={i} products={products} withPrice={!!inv} onChange={patch(r.key)} canRemove={rows.length > 1} onRemove={() => setRows((rs) => rs.filter((x) => x.key !== r.key))} />
      ))}
      <Button type="button" data-testid="multi-exit-add-row" variant="outline" className="h-11 w-full" onClick={() => setRows((rs) => [...rs, newRow()])}><Plus className="h-4 w-4 mr-1" /> Aggiungi prodotto</Button>
      <Field label="Motivo *"><Sel testId="multi-exit-reason-select" value={f.reason} onChange={set("reason")} placeholder="Scegli il motivo" options={opts(assoc?.exit_reasons)} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Chi ritira"><Input data-testid="multi-exit-pickedup-input" value={f.picked_up_by || ""} onChange={set("picked_up_by")} className="h-11" /></Field>
        <Field label="Destinatario"><Input data-testid="multi-exit-recipient-input" value={f.recipient || ""} onChange={set("recipient")} className="h-11" /></Field>
      </div>
      {events.length > 0 && <Field label="Evento collegato"><Sel testId="multi-exit-event-select" value={f.event_id} onChange={set("event_id")} placeholder="Nessuno" options={events.map((e) => ({ value: e.id, label: `${e.name} (${fmtDate(e.start)})` }))} /></Field>}
      <Field label="Giustificativo" hint="Facoltativo"><Input data-testid="multi-exit-document-input" value={f.document || ""} onChange={set("document")} className="h-11" /></Field>
      {can("invoices", "create") && (
        <div className="rounded-lg border border-slate-200 p-3 space-y-3">
          <label className="flex items-center gap-3 font-semibold text-slate-900 cursor-pointer">
            <Checkbox data-testid="multi-exit-invoice-checkbox" checked={!!inv} onCheckedChange={(c) => setInv(c ? { recipient_name: f.recipient || "", email: "", street: "", pcode: "", city: "" } : null)} />
            Crea e invia un'unica fattura
          </label>
          {inv && <InvoiceFields inv={inv} setInv={setInv} />}
          {inv && <div data-testid="multi-exit-invoice-total" className="text-right font-semibold text-slate-900">Totale fattura {fmtCHF(total)}</div>}
        </div>
      )}
      <Field label="Note"><Textarea data-testid="multi-exit-notes-input" value={f.notes || ""} onChange={set("notes")} rows={2} /></Field>
    </FormDialog>
  );
};
